import {
  getInquiryByIdAPI,
  postInquiryAnswerAPI,
} from '@/src/apis/rest/inquiry';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import SectionCard from '@/src/components/molecule/SectionCard';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import TextArea from '@/src/components/atom/TextArea';
import { useState } from 'react';
import Badge from '@/src/components/atom/Badge';
import { TOAST_CONST } from '@/src/constants/toast';
import { usePrompt } from '@/src/hooks/usePrompt';
import AlertDialog from '@/src/components/atom/AlertDialog';
import MultipleImagePreview from '@/src/components/molecule/MultipleImagePreview';

type ModalType = 'NONE' | 'SAVE_CONFIRM';

const BACK_PATH = PATH.INQUIRY_LIST;

export default function InquiryDetailPage() {
  // util
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // state
  const [modalState, setModalState] = useState<ModalType>('NONE');

  // form
  const [answerContent, setAnswerContent] = useState('');

  // query
  const { data: InquiryData, refetch } = useQuery({
    queryKey: ['inquiry', id],
    queryFn: () => getInquiryByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // mutation
  const inquiryAnswerMutation = useMutation({
    mutationFn: postInquiryAnswerAPI,
  });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleSave = () => {
    inquiryAnswerMutation.mutate(
      {
        inquiryId: Number(id),
        content: answerContent,
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          setAnswerContent('');
          refetch();
          setModalState('NONE');
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
          setModalState('NONE');
        },
      },
    );
  };
  const handleSaveClick = () => {
    setModalState('SAVE_CONFIRM');
  };
  const handleModalClose = () => {
    setModalState('NONE');
  };

  // render
  const renderBadge = () => {
    switch (InquiryData?.inquiryAnswerStatus) {
      case '답변 대기':
        return (
          <Badge badgeColor="lightRed">{InquiryData.inquiryAnswerStatus}</Badge>
        );
      case '답변 완료':
        return (
          <Badge badgeColor="lightGray">
            {InquiryData.inquiryAnswerStatus}
          </Badge>
        );
      default:
        return <Badge badgeColor="lightGray">-</Badge>;
    }
  };

  const renderContents = () => {
    switch (InquiryData?.inquiryAnswerStatus) {
      case '답변 대기':
        return (
          <TextArea
            autoFocus
            className="flex-1 overflow-auto rounded-lg border p-4"
            value={answerContent}
            onChange={e => setAnswerContent(e.target.value)}
            placeholder="답변 내용을 입력해주세요"
          />
        );
      case '답변 완료':
        return (
          <div className="flex flex-1 flex-col gap-6">
            <SectionLabel
              label="답변 관리자"
              value={InquiryData?.inquiryAnswer?.administrator?.name}
            />
            <SectionLabel
              label="답변 일시"
              value={dayjs(InquiryData?.inquiryAnswer?.createdAt).format(
                'YYYY-MM-DD HH:mm',
              )}
            />
            <SectionLabel
              direction="vertical"
              label="답변 내용"
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
              value={InquiryData?.inquiryAnswer?.content}
            />
          </div>
        );
      default:
        return <div className="flex-1 rounded-lg border" />;
    }
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>

          {InquiryData?.inquiryAnswerStatus === '답변 대기' && (
            <Button
              disabled={!answerContent}
              className="w-40"
              buttonSize="medium"
              onClick={handleSaveClick}
              isLoading={inquiryAnswerMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.save}
              </div>
            </Button>
          )}
        </div>
      </BottomFixedContainer>
    );
  };

  // usePrompt
  usePrompt(!!answerContent);

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.inquiry, NAV_CONST.inquiryDetail]}
        pageTitle={NAV_CONST.inquiryDetail}
      />
      <div className="mb-[100px] flex flex-col gap-4">
        <SectionCard
          cardTitle={
            <div className="flex w-full items-center gap-2">
              <span className="font-pretendardSemiBold text-b18sb">
                문의 내용
              </span>
              {renderBadge()}
            </div>
          }
        >
          <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
            <article className="flex flex-col gap-6">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
                <div className="flex flex-col gap-6">
                  <SectionLabel
                    label="문의 유형"
                    value={InquiryData?.inquiryCategory}
                  />
                  <SectionLabel
                    label="닉네임"
                    value={InquiryData?.customer?.nick}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <SectionLabel
                    label="등록 일시"
                    value={
                      InquiryData?.createdAt
                        ? dayjs(InquiryData.createdAt).format(
                            'YYYY-MM-DD HH:mm:ss',
                          )
                        : '-'
                    }
                  />
                  <SectionLabel
                    label="등록자"
                    value={InquiryData?.customer?.name}
                  />
                </div>
              </div>
              <p className="h-[413px] overflow-auto rounded-lg border p-4 text-gray6">
                {InquiryData?.content}
              </p>
              <SectionLabel
                label="첨부 이미지"
                value={
                  InquiryData?.inquiryMedias?.length ? (
                    <MultipleImagePreview
                      images={InquiryData.inquiryMedias}
                      width={163}
                      height={163}
                      isPreviewRatioFixed={false}
                      previewWidth={500}
                    />
                  ) : (
                    '없음'
                  )
                }
              />
            </article>
            <div className="border-r border-r-gray1" />
            <article className="flex flex-col gap-6">
              <h3 className="text-h20sb">문의 답변 내용</h3>
              {renderContents()}
            </article>
          </div>
        </SectionCard>
      </div>
      {renderBottomFixedContent()}

      {/* 모달 */}
      <AlertDialog
        open={modalState === 'SAVE_CONFIRM'}
        title="1:1문의 답변 등록"
        onCancel={handleModalClose}
        onOk={handleSave}
        okButtonProps={{
          isLoading: inquiryAnswerMutation.isPending,
        }}
      >
        등록한 답변은 수정이 불가능합니다. 답변을 등록하시겠습니까?
      </AlertDialog>
    </>
  );
}
